import * as React from 'react';
import * as PropTypes from 'prop-types';
import { classNames, focusFirstFocusableChild } from '@progress/kendo-react-common';
/**
 * Represents the [KendoReact AppBarSpacer component]({% slug contentarrangement_appbar %}#toc-defining-spacings).
 * Used to give additional white space between the AppBar sections and provides a way for customizing its width.
 *
 * @example
 * ```jsx
 *
 * const App = () => {
 *   return (
 *       <AppBar>
 *           <AppBarSection>
 *               <span className="k-icon k-i-menu" />
 *           </AppBarSection>
 *
 *           <AppBarSpacer style={{ width: 8 }} />
 *
 *           <AppBarSection>
 *               <h1 className="title">{tc.text} AppBar</h1>
 *           </AppBarSection>
 *
 *           <AppBarSpacer />
 *
 *           <AppBarSection>
 *               <BadgeContainer>
 *                   <span className="k-icon k-i-bell" />
 *                   <Badge themeColor="info" shape="dot" />
 *               </BadgeContainer>
 *           </AppBarSection>
 *      </AppBar>
 *   );
 * };
 *    ReactDOM.render(<App />, document.querySelector('my-app'));
 * ```
 */

export var AppBarSpacer = React.forwardRef(function (props, target) {
  var children = props.children,
      className = props.className,
      style = props.style;
  var elementRef = React.useRef(null);
  var focusElement = React.useCallback(function () {
    if (elementRef.current) {
      focusFirstFocusableChild(elementRef.current);
    }
  }, []);
  var getImperativeHandle = React.useCallback(function () {
    return {
      element: elementRef.current,
      focus: focusElement
    };
  }, [focusElement]);
  React.useImperativeHandle(target, getImperativeHandle);
  var spacerClasses = React.useMemo(function () {
    return classNames('k-appbar-spacer', {
      'k-appbar-spacer-sized': style && style.width && style.width !== null
    }, className);
  }, [className, style]);
  var spacerStyles = React.useMemo(function () {
    return {
      flexBasis: style && style.width ? style.width : undefined
    };
  }, [style]);
  return React.createElement("span", {
    className: spacerClasses,
    style: spacerStyles
  }, children);
});
AppBarSpacer.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  style: PropTypes.object
};
AppBarSpacer.displayName = 'KendoAppBarSpacer';